import { useEffect, useRef, useState } from "react";
import "./visits.css";
import { serverFetch } from "../../server";
import { Badge, Button, Checkbox, DatePicker, FloatButton, Input, message, Select, Table, Tag, Tooltip } from "antd";
import VisitOneAdd from "./visit-one-add";
import VisitClientAdd from "./visit-client-add";
import VisitBody from "./visit-body";
import dayjs from "dayjs";
import { Scan, SportTooltip } from "../../controls";
import { fmtDateTimeFull, frmSymaNull, strDate2Server } from "../../extensions/utils";
import { hasSubstring } from "../../extensions/helper";
import { HelpButton, isMobile, menuSize, RefreshButton, ScanButton } from "../../extensions/design";
import VisitItem from "./visit-item";
import { ClockCircleOutlined, PlusOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import VisitFilter from "./visit-filter";
import ScanMobile from "../../controls/scan/scan-mobile";
import { BtnSettingsTable, tblGetColumns } from "../../controls/table-settings.js/table-settings";
import { useLocalStorageFilter, applyDateRoll } from "@/extensions/use-local-storage-filter";

const vidOptions = [
  { label: "Клиенты в клубе", value: 0 },
  { label: "Визиты за период", value: 1 },
]

const tableName = "visits";

const Visits = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const inputRef = useRef(null);
  const [selected, setSelected] = useState();
  // Фильтр журнала визитов
  // vid = 0 - клиенты в клубе
  // vid = 1 - клиенты за период dateBeg - dateEnd
  const [filter, setFilter] = useLocalStorageFilter(
    'visitsFilter',
    () => ({ vid: 0, dateBeg: dayjs(), dateEnd: dayjs(), searchText: '' }),
    (parsed) => {
      const { dateFrom: dateBeg, dateTo: dateEnd } = applyDateRoll(
        parsed.dateEnd ? dayjs(parsed.dateEnd) : null,
        dayjs(),
        dayjs(),
        parsed.dateBeg ? dayjs(parsed.dateBeg) : null,
      );
      return {
        vid: 0,
        dateBeg: typeof dateBeg === 'string' ? dayjs(dateBeg) : dateBeg,
        dateEnd: typeof dateEnd === 'string' ? dayjs(dateEnd) : dateEnd,
        searchText: parsed.searchText || '',
      };
    },
    (filter) => ({
      vid: 0,
      dateBeg: filter.dateBeg?.format('YYYY-MM-DD'),
      dateEnd: filter.dateEnd?.format('YYYY-MM-DD'),
      searchText: filter.searchText || '',
    }),
  );
  // Режим работы - правая часть
  // 0 - отображение выделенного визита
  // 1 - добавление разового визита
  // 2 - добавление визита клиента
  const divRef = useRef(null);
  // Режим создания визита
  // 1 - Разовый
  // 2 - Визит клиента
  const [mode, setMode] = useState();
  // Просканированный # клиента для создания визита
  const [clientId, setClientId] = useState();
  // Код карты для разового визита после сканирования
  const [scanCardCode, setScanCardCode] = useState();

  const [showFilter, setShowFilter] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [tableKey, setTableKey] = useState(0); // Для принудительного обновления таблицы

  const getData = newFilter => {
    const filterAsk = newFilter ? newFilter : filter;
    serverFetch(`visit?vid=${filterAsk.vid}&dateBeg=${strDate2Server(filterAsk.dateBeg)}&dateEnd=${strDate2Server(filterAsk.dateEnd)}`)
      .then(data => setItems(data))
      .catch(e => message.error(e.userMessage));
  }

  useEffect(getData, [filter]);

  // selectedId, onSelect, setMode, onRefresh, setClient

  const renderColClient = record => {
    const roleIcons = [];
    if (record.isRepresentative) {
      roleIcons.push(
        <SportTooltip
          key="role-rep"
          title="Представитель группы"
          placement="top"
          getPopupContainer={() => document.body}
        >
          <span className="client-role-icon client-role-icon_representative">
            <TeamOutlined />
          </span>
        </SportTooltip>
      );
    }
    if (record.isFamilyMember) {
      roleIcons.push(
        <SportTooltip
          key="role-fam"
          title="Участник группы"
          placement="top"
          getPopupContainer={() => document.body}
        >
          <span className="client-role-icon client-role-icon_family">
            <UserOutlined />
          </span>
        </SportTooltip>
      );
    }
    if (record.recordId) {
      return <div className="visit-list-client">
        <img src="/images/calendar.svg" alt="card"/><div>{record.clientName}</div>{roleIcons}
      </div>
    }
    else if (record.clientId) {
      return <div className="visit-list-client">
        <img src="/images/card.svg" alt="card"/><div>{record.clientName}</div>{roleIcons}
      </div>
    }
    else {
      return <div className="visit-list-client">
        <img src="/images/mask.svg" alt="mask"/>
        {/* <div>{record.comment}</div> */}
      </div>
    }
  }

  const getPenaltyVid = record => Number(record.penaltyVid ?? record.PenaltyVid ?? 0);
  const getPlanTime = record => record.planTime ?? record.PlanTime;
  const getTimePenalty = record => Number(record.timePenalty ?? record.TimePenalty ?? 0);
  const getPenaltyClassName = record => !record.finish && getPlanTime(record)
    ? `visit-penalty-time visit-penalty-time-${getPenaltyVid(record)}`
    : "";

  const renderColFinish = (_, record) => {
    const value = record.finish || getPlanTime(record);
    return <span className={getPenaltyClassName(record)}>{fmtDateTimeFull(value)}</span>;
  }

  const renderColTimePenalty = (_, record) => {
    const timePenalty = getTimePenalty(record);
    if (!timePenalty) return null;
    
    return <span className={`visit-time-penalty visit-time-penalty-${getPenaltyVid(record)}`}>
      <ClockCircleOutlined /> {timePenalty}
    </span>;
  }
  
  const columns = isMobile ? [
    { title: "#", dataIndex: "id",  },
    { title: <img src="/images/lock.svg" className="table-header-img"/>,  dataIndex: "finish", render: e => <Checkbox checked={e} />, width: 50, },
    { title: "Клиент", render: renderColClient, width: 300 },
    { title: "Долг", dataIndex: "dolg", align: "right", render: frmSymaNull },
    { title: "Ключ", dataIndex: "keyName", align: "center" },
  ] : [
    { title: "#", dataIndex: "id", minWidth: 40, },
    { title: <img src="/images/lock.svg" className="table-header-img" alt="lock"/>, hint: "Визит закрыт",  dataIndex: "finish", render: e => <Checkbox checked={e} />, minWidth: 24,},
    { title: "Клиент", render: renderColClient, minWidth: 100, },
    { title: "Долг", dataIndex: "dolg", align: "right", render: frmSymaNull, minWidth: 50, },
    { title: <img src="/images/key.svg" className="table-header-img" alt="key"/>, hint: "Ключ", dataIndex: "keyName", align: "center", minWidth: 24, },
    { title: "Депозит", dataIndex: "depos", align: "right", render: frmSymaNull, hidden: true, minWidth: 100, },
    { title: "Вход", dataIndex: "start", render: fmtDateTimeFull, minWidth: 70, },
    { title: "Выход", dataIndex: "finish", render: renderColFinish, minWidth: 70, },
    { title: <ClockCircleOutlined />, hint: "Минут превышения", render: renderColTimePenalty, minWidth: 40, align: "center" },
    { title: "Услуга", dataIndex: "productName", minWidth: 100 },
    { title: <img src="/images/card.svg" className="table-header-img" alt="card"/>, hint: "Карта клиента", dataIndex: "clientCard", minWidth: 100 },
    { title: "Доп. карта", dataIndex: "cardCode", minWidth: 100 },
    { title: "Пользователь", dataIndex: "userName", hidden: true, minWidth: 100 },
    { title: "Комментарий", dataIndex: "comment", minWidth: 100 }
  ]

  const filteredItems = items
    .filter(item => hasSubstring(filter.searchText, item.clientName) 
      || hasSubstring(filter.searchText, item.clientPhone) 
      || hasSubstring(filter.searchText, item.clientCard)
      || hasSubstring(filter.searchText, item.cardCode)
      || hasSubstring(filter.searchText, item.keyName));

  const onScan = value => {
    setShowScan(false);
    if (!value) return;
    setLoading(true);
    serverFetch(`visit/GetByCard?code=${encodeURIComponent(value)}`)
      .then(data => {
        setLoading(false);
        const visit = data?.visit ?? data?.Visit;
        const client = data?.client ?? data?.Client;
        if (visit) {
          setMode(null);
          setClientId();
          setScanCardCode();
          setSelected(visit.id ?? visit.Id);
          return;
        }
        setSelected(null);
        if (client) {
          setScanCardCode();
          setClientId(client.id ?? client.Id);
          setMode(2);
          return;
        }
        setClientId();
        setScanCardCode(value);
        setMode(1);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  }

  const onSelect = item => setSelected(item?.id);

  const onChangeFilter = value => setFilter({ ...filter, ...value });

  const headerSize =  divRef.current?.offsetTop ?? 0;
  const tableHeight = divRef.current ? (isMobile ? (document.documentElement.clientHeight - headerSize - menuSize) :
    (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY - 1)
  ) : 0;

  const tableWidth = isMobile ? document.documentElement.clientWidth : (window.innerWidth - 16 - (divRef.current?.getBoundingClientRect().left ?? 0));

  const onCloseEdit = (refresh, visitId) => {
    setSelected(visitId);
    setMode(null);
    setScanCardCode();
    setClientId();
    refresh && getData();
  }

  const onTableSettingsChange = () => {
    setTableKey(prev => prev + 1); // Принудительно обновляем таблицу
  }

const vidTooltipTitle = <div>
  <div><strong>Фильтр визитов</strong></div>
  <div>Клиенты в клубе - незакрытые визиты</div>
  <div>Визиты за период - визиты за период</div>
</div>

const divTools = isMobile ? <></> : <div className="col-one-title">
    <div><SportTooltip title={vidTooltipTitle} placement="top">Вид</SportTooltip></div>
    <Select options={vidOptions} value={filter.vid} onChange={e => onChangeFilter({vid: e})} style={{minWidth: 200}}/>
    {filter.vid ? <>
      <DatePicker value={filter.dateBeg} format="DD.MM.YYYY" allowClear={false} onChange={e => onChangeFilter({dateBeg: e})} style={{width: 135}}/>
      <DatePicker value={filter.dateEnd} format="DD.MM.YYYY" allowClear={false} onChange={e => onChangeFilter({dateEnd: e})} style={{width: 135}}/>
    </> : null}
    {/* <RefreshButton onClick={() => getData()} /> */}
    <Input placeholder="Введите ФИО, телефон, карту, ключ" value={filter.searchText} onChange={e => onChangeFilter({searchText: e.target.value})} allowClear ref={inputRef} style={{maxWidth: 350}}/>
    <ScanButton
      title="Просканируйте карту клиента для создения или изменения визита"
      onScan={onScan}
      id="tour-item-visits-scan-button"
      style={{ marginLeft: 0 }}
    />
    <SportTooltip title="Визит клиента"><Button onClick={() => {setMode(2); setClientId(); setScanCardCode();}}>Визит</Button></SportTooltip>
    <SportTooltip title="Разовый визит"><Button onClick={() => {setMode(1); setScanCardCode();}}>Разовый</Button></SportTooltip>
    <BtnSettingsTable name={tableName} columns={columns} onSettingsChange={onTableSettingsChange} />
    <HelpButton article="zhurnal-vizity" />
    {filteredItems.length ? <Tag color="#2f6f00">{filteredItems.length} визитов</Tag> : null}
  </div>

const divItems = isMobile ? 
<div style={{ position: "relative", height: window.innerHeight - 45.8 }}>
    <div className="col-one-title-upper-menu">
      <div  className="rounded-search-field" style={{padding: "0px", height: "35px"}}>
        <Input placeholder="Поиск" value={filter.searchText} onChange={e => onChangeFilter({searchText: e.target.value})} allowClear className="search-str" style={{ paddingLeft: "16px", borderRadius:"100px", width: "100%", minHeight: "35px" }}/>
      </div>
      <div className="rounded-filter-button" onClick={() => setShowFilter(true)}>
        <img src="/images/filter.svg" className="mobile-top-img" alt="filter" style={{ width: "20px", height: "20px" }} />
      </div>
    </div>

    <div className="visit-items" style={{height: "100%", overflowY: "scroll", overflowX: "clip", paddingTop: "55px"}}>
      {filteredItems.map(item => <VisitItem item={item} key={item.id} onClick={() => setSelected(item.id)}/> )}
    </div>
  </div>
   : 
  <div ref={divRef} style={{ width: tableWidth }} className="col1-col">
    <Table key={tableKey} columns={tblGetColumns(columns, tableName)} dataSource={filteredItems} size="small"
      rowSelection={{ type: 'radio', onSelect: onSelect, selectedRowKeys: [selected], renderCell: checked => <div className={checked ? "list-indicator" : ""} /> }}
      rowKey="id"
      pagination={false} 
      onRow={(record) => ({onClick: () => {onSelect(record)}})}
      loading={loading}
      scroll={{ y: tableHeight }}
      tableLayout="auto"
    />
  </div>

return<div className="col-one-main">
    {divTools}  
    {divItems}  
    {isMobile && !selected && mode != 2 && mode != 1 &&  <>
      <FloatButton.Group trigger="click" icon={<PlusOutlined />}>
        <FloatButton style={{ insetBlockEnd: 196 }} icon={<img src="/images/scan.svg" alt="scan" style={{width : 24}}/>} onClick={() => setShowScan(true)} />
        <FloatButton style={{ insetBlockEnd: 108 }} onClick={() => {setMode(2); setClientId(); setScanCardCode();}} icon={<img src="/images/card.svg" alt="card" style={{width : 24}}/>} />
        <FloatButton onClick={() => {setMode(1); setScanCardCode();}} icon={<img src="/images/mask.svg" alt="mask" style={{width : 24}}/>}/>
      </FloatButton.Group>
    </>}
    {(isMobile || selected || mode) ? null : <Scan onScan={onScan}/>}
    {selected && <VisitBody id={selected} onClose={onCloseEdit} onRefresh={getData} />}
    {mode === 1 && <VisitOneAdd onClose={onCloseEdit} defaultCardCode={scanCardCode}/>}
    {mode === 2 && <VisitClientAdd onClose={onCloseEdit} defaultClientId={clientId}/>}
    {showFilter && <VisitFilter current={filter} onClose={ newFilter => { setShowFilter(false); newFilter && setFilter(newFilter); newFilter && getData(newFilter) }} />}
    {showScan && <ScanMobile onScan={onScan} onClose={() => setShowScan(false)}/>} 
  </div>
}

export default Visits;
